import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import loadAddressFields from "@insite/client-framework/Store/Data/AddressFields/Handlers/LoadAddressFields";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateBillTo from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateBillTo";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
import BillingAddress, { BillingAddressStyles } from "@tSurya/Overrides/Widgets/CheckoutShipping/BillingAddress";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const addressFieldsDataView = getAddressFieldsDataView(state);

    return {
        cart,
        countries: getCurrentCountries(state),
        billToAddressFields: addressFieldsDataView.value?.billToAddressFields,
        shouldLoadAddressFields: !addressFieldsDataView.value && !addressFieldsDataView.isLoading,
        billToState: getBillToState(state, cart?.billToId),
        cartPageLink: getPageLinkByPageType(state, "CartPage")?.url,
    };
};

const mapDispatchToProps = {
    updateBillTo,
    loadAddressFields,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CheckoutBillingAddressCardStyles {
    wrapper?: InjectableCss;
    loadingWrapper?: InjectableCss;
    billingAddress?: BillingAddressStyles;
}

export const checkoutBillingAddressCardStyles: CheckoutBillingAddressCardStyles = {
    wrapper: {
        css: css`
            margin-bottom: 30px;
        `,
    },
    loadingWrapper: {
        css: css`
            display: flex;
            height: 200px;
            justify-content: center;
            align-items: center;
        `,
    },
};

const styles = checkoutBillingAddressCardStyles;

const CheckoutBillingAddressCard: FC<Props> = ({
    cart,
    billToState,
    countries,
    billToAddressFields,
    shouldLoadAddressFields,
    updateBillTo,
    loadAddressFields,
    cartPageLink,
    history,
}) => {
    useEffect(() => {
        if (shouldLoadAddressFields) {
            loadAddressFields();
        }
    }, [shouldLoadAddressFields]);

    if (!cart || !billToState.value || !countries || !billToAddressFields) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.loadingWrapper}>
                    <LoadingSpinner />
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    const changeBillingAddressHandler = (address: BillToModel, isEdit: boolean) => {
        updateBillTo({
            billTo: address,
            isEdit,
            onSuccess: hasCartlines => {
                if (!hasCartlines) {
                    history.push(cartPageLink!);
                }
            },
        });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <BillingAddress
                address={billToState.value}
                onChange={changeBillingAddressHandler}
                countries={countries}
                addressFieldDisplayCollection={billToAddressFields}
                extendedStyles={styles.billingAddress}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutBillingAddressCard));
